import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getModelsByMakeDropdown, validateZip } from '@/actions/remoteActions';
import { handleSearchBadgeSubmit } from '@/actions/heroSearchActions';
import { makeDropDownMask } from '@/utilities/dataTransformers';
import { callToActionAnalytics } from '@/analytics';
import { recentSearchDuck } from '@/ducks';

export const VehicleSearchContext = createContext();

export const useVehicleSearch = () => {
    const context = useContext(VehicleSearchContext);
    if (context === undefined) {
        throw new Error('useVehicleSearch must be used within a VehicleSearchProvider');
    }
    return context;
};

export const useMakeDropdown = () => {
    const { make, setMake, vehicleMakes, setModelOptions } = useVehicleSearch();

    const handleMakeChange = async (event, analyticsData) => {
        const selectedMake = event.target.value;
        const selectedMakeLabel = event.target.options[event.target.selectedIndex].text;
        callToActionAnalytics.fireMakeOnChangeClick(event, { make: selectedMake, ...analyticsData });
        setMake({ value: selectedMake, label: selectedMakeLabel });
        const models = await getModelsByMakeDropdown(selectedMake);
        setModelOptions(models);
    };

    return { make, makeOptions: makeDropDownMask(vehicleMakes), handleMakeChange };
};

export const useModelDropdown = () => {
    const { model, setModel, modelOptions } = useVehicleSearch();

    const handleModelChange = (event, analyticsData) => {
        const selectedModel = event.target.value;
        callToActionAnalytics.fireModelOnChangeClick(event, { model: selectedModel, ...analyticsData });

        // allows inclusion of add'l model data (i.e. series) needed for analytics
        const allModelsGroup = modelOptions.find((option) => option.optgroup === 'All Models').options;
        setModel(allModelsGroup.find((option) => option.value === selectedModel) ?? {});
    };

    return { model, handleModelChange };
};

export const useModelOptions = () => {
    const { modelOptions } = useVehicleSearch();
    return modelOptions;
};

export const useZipcodeInput = () => {
    const { zipcode, setZipcode, zipValid, setZipValid } = useVehicleSearch();

    const handleZipcodeChange = async (event) => {
        const enteredZipcode = event.target.value;
        if (enteredZipcode.length < 5) return;
        try {
            const isValid = await validateZip(enteredZipcode);
            if (isValid) {
                setZipcode(enteredZipcode);
                setZipValid(true);
                callToActionAnalytics.fireZipOnChangeEvent(event, { zipcode: enteredZipcode });
            } else {
                // Handle invalid zipcode case
                console.error('Invalid Zipcode');
            }
        } catch (error) {
            console.error('Error validating zipcode:', error);
        }
    };

    return { zipcode, zipValid, handleZipcodeChange };
};

export const useSearchSubmit = () => {
    const { make, model, zipcode, zipValid, submitAnalyticsData, searchQueries } = useVehicleSearch();

    const handleSearchSubmit = (data, resetForm, invalidateForm) => {
        // resetForm and invalidateForm are from formsy-react
        if (!zipValid) {
            invalidateForm({
                zipcode: 'Enter a valid ZIP Code',
            });
            return;
        }
        const checkBoxData = Object.keys(data).find((key) => key.includes('searchBadgeCheckbox'));
        const searchParams = {
            make,
            model,
            zip: zipcode,
            ...(checkBoxData && { marketExtension: data[checkBoxData] ? 'only' : 'off' }), // for how-to-buy-cars page
        };

        callToActionAnalytics.fireSearchSubmit({ ...searchParams, ...submitAnalyticsData });
        handleSearchBadgeSubmit({ ...searchParams, ...searchQueries });
    };

    return { handleSearchSubmit };
};

export function VehicleSearchProvider({ children, data = {} }) {
    const { vehicleMakes, userZipCode, submitAnalyticsData = {}, searchQueries = {} } = data;
    const [make, setMake] = useState('');
    const [model, setModel] = useState('');
    const [modelOptions, setModelOptions] = useState([{ label: 'Any Model', value: '' }]);
    const [zipcode, setZipcode] = useState(userZipCode || '');
    const [zipValid, setZipValid] = useState(!!userZipCode);

    const recentSearch = useSelector(recentSearchDuck.selectors.getRecentSearchQuery);

    useEffect(() => {
        // prepopulate make and model dropdowns from recent search
        async function fetchData() {
            if (recentSearch.makeCode) {
                setMake({ value: recentSearch.makeCode });
                const models = await getModelsByMakeDropdown(recentSearch.makeCode);
                setModelOptions(models);
            }
            if (recentSearch.modelCode) {
                setModel({ value: recentSearch.modelCode });
            }
        }
        fetchData();
    }, [recentSearch]);

    const value = React.useMemo(
        () => ({
            make,
            setMake,
            model,
            setModel,
            vehicleMakes,
            modelOptions,
            setModelOptions,
            zipcode,
            setZipcode,
            zipValid,
            setZipValid,
            submitAnalyticsData,
            searchQueries,
        }),
        [make, model, vehicleMakes, modelOptions, zipcode, zipValid, submitAnalyticsData, searchQueries],
    );

    return (
        <VehicleSearchContext.Provider value={value}>
            {children}
        </VehicleSearchContext.Provider>
    );
}
